import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from './AuthContext';

// Public demo studio support. /api/demo/accounts only answers on the demo
// tenant's host, so on every real studio this stays inert (accounts === null).

export type DemoRole = 'owner' | 'instructor' | 'student';

export interface DemoAccount {
  role: DemoRole;
  email: string;
  name: string;
  blurb: string;
  password: string;
}

/** Where each role lands after a one-click login. */
export const DEMO_START: Record<DemoRole, string> = {
  student: '/library/moves',
  instructor: '/coach',
  owner: '/admin',
};

interface DemoContextType {
  accounts: DemoAccount[] | null;
  /** Role of the signed-in demo account, if the current user is one. */
  currentRole: DemoRole | null;
  entering: DemoRole | null;
  error: string | null;
  enterAs: (role: DemoRole) => Promise<void>;
}

const DemoContext = createContext<DemoContextType>({
  accounts: null,
  currentRole: null,
  entering: null,
  error: null,
  enterAs: async () => {},
});

export const DemoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<DemoAccount[] | null>(null);
  const [entering, setEntering] = useState<DemoRole | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get('/demo/accounts')
      .then((res) => setAccounts(res.data))
      .catch(() => setAccounts(null));
  }, []);

  const currentRole =
    accounts?.find((a) => a.email.toLowerCase() === user?.email?.toLowerCase())?.role ?? null;

  const enterAs = useCallback(async (role: DemoRole) => {
    const account = accounts?.find((a) => a.role === role);
    if (!account) return;
    setEntering(role);
    setError(null);
    try {
      if (user) logout();
      await login(account.email, account.password);
      navigate(DEMO_START[role]);
    } catch {
      setError('The demo is resetting — try again in a minute.');
    } finally {
      setEntering(null);
    }
  }, [accounts, user, login, logout, navigate]);

  return (
    <DemoContext.Provider value={{ accounts, currentRole, entering, error, enterAs }}>
      {children}
    </DemoContext.Provider>
  );
};

export function useDemo(): DemoContextType {
  return useContext(DemoContext);
}
